// WP-C1.6 (RM-2/3/4, S-36): ad-hoc reminder queries/mutations.
import {
  reminderListResponseSchema,
  reminderResponseSchema,
  type ReminderCreateRequest,
  type ReminderResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const REMINDERS_KEY = ['reminders'] as const;

export function useReminders(status?: string): UseQueryResult<ReminderResponse[]> {
  const qs = status !== undefined ? `?status=${status}` : '';
  return useQuery({
    queryKey: [...REMINDERS_KEY, status],
    queryFn: async () =>
      reminderListResponseSchema.parse(await api.get(`/api/v1/reminders${qs}`)).reminders,
  });
}

function useInvalidateReminders(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: REMINDERS_KEY });
  };
}

export function useCreateReminder(): UseMutationResult<
  ReminderResponse,
  Error,
  ReminderCreateRequest
> {
  const invalidate = useInvalidateReminders();
  return useMutation({
    mutationFn: async (input) =>
      reminderResponseSchema.parse(await api.post('/api/v1/reminders', input)),
    onSuccess: invalidate,
  });
}

export function useCancelReminder(): UseMutationResult<ReminderResponse, Error, string> {
  const invalidate = useInvalidateReminders();
  return useMutation({
    mutationFn: async (id) =>
      reminderResponseSchema.parse(await api.post(`/api/v1/reminders/${id}/cancel`)),
    onSuccess: invalidate,
  });
}

export function useSnoozeReminder(): UseMutationResult<
  ReminderResponse,
  Error,
  { id: string; minutes: number }
> {
  const invalidate = useInvalidateReminders();
  return useMutation({
    mutationFn: async ({ id, minutes }) =>
      reminderResponseSchema.parse(await api.post(`/api/v1/reminders/${id}/snooze`, { minutes })),
    onSuccess: invalidate,
  });
}
